/**
 * The resolver outbox — the vocabulary shared by its writer and its poller.
 *
 * Every write that affects resolution also writes a `resolver_outbox` row in the
 * same transaction (§3.3). Resolver nodes poll `seq > last`, apply to the SQLite
 * mirror and persist the new `last_seq`. The op is the only instruction the poller
 * gets, so the API writer and the poller have to spell it identically — hence a
 * constant rather than a string literal at each end.
 */
import { z } from "zod";
import { DOMAIN_KINDS, DOMAIN_STATUSES } from "./serving.js";
/**
 * Mirrors `0016`'s `domains_hostname_check`, so a payload no `domains` row could have
 * produced is refused at the poller rather than written into the mirror.
 */
const outboxHostname = z
    .string()
    .min(4)
    .max(253)
    .refine((h) => h === h.toLowerCase(), { message: "hostname must be lowercase" });
/**
 * Operations the outbox carries today.
 *
 * `delete` covers both a genuine delete and a tombstone: a tombstoned product keeps
 * an entry (§3.4 step 2) and is therefore an `upsert` whose entry has
 * `tombstone: true`. A `delete` row means the GTIN should resolve as unknown, which
 * is a state a printed code should almost never reach.
 *
 * `packages/db` asserts this list equals migration `0002`'s `CHECK` constraint, the
 * same treatment `TIERS` gets and for the same reason.
 *
 * `domain` names a *path* rather than a verb — it targets the mirror's `domains` table
 * and carries its own upsert/delete in {@link domainOpPayloadSchema}. That is
 * {@link RESERVED_SERIAL_LINKS_OUTBOX_OP}'s shape, applied to the first op that needed
 * it. A `domain` row carries no `gtin14`; `0021` is where that became legal.
 */
export const OUTBOX_OPS = ["upsert", "delete", "domain"];
/**
 * The op that targets the mirror's `domains` table, named rather than spelled at each
 * use — the poller switches on it and the writer emits it.
 */
export const DOMAIN_OUTBOX_OP = "domain";
/**
 * What a `domain` outbox row carries.
 *
 * `hostname` is the key both ways: `0016` makes it globally unique, so a delete needs
 * nothing else and an org id would be a second thing that can disagree.
 */
export const domainOpPayloadSchema = z.discriminatedUnion("action", [
    z.object({
        action: z.literal("upsert"),
        hostname: outboxHostname,
        kind: z.enum(DOMAIN_KINDS),
        status: z.enum(DOMAIN_STATUSES),
        orgId: z.uuid(),
    }),
    z.object({
        action: z.literal("delete"),
        hostname: outboxHostname,
    }),
]);
/**
 * Reserved for the serial-scoped mirror path — **a name, not a feature** (§15.8,
 * ruled 2026-08-03).
 *
 * A DSCSA-style tenant would carry serial-level links at a cardinality that does not
 * belong in `resolver_entries`, where one row holds one GTIN's whole linkset and the
 * cap is 500 links. That path gets its own mirror table and its own outbox op so the
 * poller can tell the two payload shapes apart — and it is built when such a tenant
 * actually appears, not before.
 *
 * Reserving the names now costs nothing and buys one thing: neither name can be
 * taken for something else in the meantime, which is the failure this reservation
 * exists to prevent. Both are deliberately **absent** from {@link OUTBOX_OPS} and
 * from the migration's `CHECK` constraint — a reserved op is not an accepted op, and
 * a row carrying one today is a bug the database should reject.
 */
export const RESERVED_SERIAL_LINKS_TABLE = "serial_links";
/**
 * The outbox op that will carry serial-scoped payloads. See
 * {@link RESERVED_SERIAL_LINKS_TABLE}.
 *
 * One op, not a pair: it names the *path* the payload targets, leaving upsert versus
 * delete to the payload itself. If the path turns out to need a second op when it is
 * built, it is reserved then — guessing at the shape of unbuilt work is how a
 * reservation becomes a design.
 */
export const RESERVED_SERIAL_LINKS_OUTBOX_OP = "serial_link";
//# sourceMappingURL=outbox.js.map